import { useCallback, useEffect, useState } from 'react'
import { kbApi } from '@carrent/api'

/**
 * Группы пользователя на его карточке.
 *
 * Членство видно и правится с обеих сторон — со страницы группы
 * и отсюда: администратор, открывший человека, не должен помнить,
 * что состав живёт где-то ещё.
 *
 * Региональные группы показаны, но без кнопки «убрать»: они следуют
 * за регионами водителя, и снять членство можно только сменив регион.
 */
export function UserGroups({ userId, userRegionIds = [] }) {
  const [groups, setGroups] = useState([])
  const [memberships, setMemberships] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await kbApi.groups()
      const all = r.data?.data || []
      setGroups(all)

      // Состав хранится у групп; для карточки нужен обратный срез.
      const custom = all.filter((g) => g.kind === 'custom')
      const lists = await Promise.all(
        custom.map((g) => kbApi.groupMembers(g.id).then(
          (res) => [g.id, (res.data?.data?.members || []).some((m) => m.id === userId)],
        ).catch(() => [g.id, false])),
      )
      setMemberships(new Set(lists.filter(([, isMember]) => isMember).map(([id]) => id)))
      setError('')
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось загрузить группы')
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => { load() }, [load])

  const toggle = async (group) => {
    setBusy(true)
    try {
      if (memberships.has(group.id)) await kbApi.removeGroupMember(group.id, userId)
      else await kbApi.addGroupMember(group.id, userId)
      await load()
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось изменить членство')
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div className="groups__loading"><div className="spinner" /></div>
  if (error) return <p className="field__error">{error}</p>

  const custom = groups.filter((g) => g.kind === 'custom')
  const regional = groups.filter(
    (g) => g.kind === 'region' && userRegionIds.includes(g.region_id),
  )

  return (
    <div className="user-groups">
      {regional.length > 0 && (
        <div className="user-groups__auto">
          <span className="user-groups__auto-label">Автоматически, по регионам:</span>
          {regional.map((g) => (
            <span key={g.id} className="user-groups__chip user-groups__chip--auto">
              {g.name}
            </span>
          ))}
        </div>
      )}

      {custom.length === 0 ? (
        <p className="groups__empty">Пользовательских групп пока нет.</p>
      ) : (
        <ul className="user-groups__list">
          {custom.map((g) => {
            const inGroup = memberships.has(g.id)
            return (
              <li key={g.id} className="user-groups__row">
                <span className="user-groups__name">{g.name}</span>
                <button
                  type="button"
                  className={`btn btn--sm ${inGroup ? 'btn--ghost' : 'btn--secondary'}`}
                  disabled={busy}
                  onClick={() => toggle(g)}
                >
                  {inGroup ? 'Убрать' : 'Добавить'}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
